import React, { useCallback, useState } from 'react';
import {
    Alert,
    AllEntitiesDropdown,
    AutoGrid,
    Button,
    Checkbox,
    DialogV2,
    SelectField,
} from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { Box, CircularProgress, FormLabel, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { CheckCircle } from '@mui/icons-material';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import to from 'await-to-js';
import { NetworkRequestState } from 'c-data-layer';
import type { AxiosResponse } from 'axios';
import { PermissionName } from 'c-sdk';
import { IfHasAllPermissions } from 'c-auth-module/Components';

interface AllocationDialogProps {
    ids: number[];
    setIds: (ids: number[]) => void;
    filters: any;
    setConflictedIds: (ids: number[] | null) => void;
    filtered: {
        value: boolean;
        setFalse: () => void;
        setTrue: () => void;
    };
    allocationState: {
        value: boolean;
        setFalse: () => void;
        setTrue: () => void;
    };
}

const allocationPerms = [PermissionName.AdminDisplaysAllocation];

const AllocationDialog: React.FC<AllocationDialogProps> = ({
    ids,
    setIds,
    filters,
    setConflictedIds,
    filtered,
    allocationState,
}) => {
    const t = useCommonTranslation();
    const [override, setOverride] = useState(false);
    const [error, setError] = useState<any | null>(null);
    const [allocationResponse, setResponse] = useState<AxiosResponse<unknown> | null>(null);
    const [responseDialogOpen, setResponseDialogOpen] = useState(false);

    const {
        start,
        requestState,
        reset: requestReset,
    } = useAPIClientRequest(ApiClient.Entities.Displays_Screen.allocateAndDisAllocate);

    const { handleSubmit, control, watch, reset } = useForm({});

    const allocationType = watch('allocation_to_type');
    const typeOfAllocation = watch('allocation_type');
    const screenToAllocateType = watch('screen_to_allocate_type');

    const handleFormSubmit = useCallback(
        async data => {
            filtered.setFalse();
            const dataToSubmit = { ...data };
            if (dataToSubmit.screen_to_allocate_type === 'selected') {
                delete dataToSubmit.filter;
                dataToSubmit.display_ids = ids;
            } else {
                delete dataToSubmit.display_ids;
                if (filters) {
                    Object.assign(dataToSubmit, filters);
                }
            }
            if (allocationType === 'packs') {
                dataToSubmit.allocation_to_type_id = dataToSubmit.allocation_to_type_id_packs;
            }
            if (allocationType === 'tags') {
                dataToSubmit.allocation_to_type_id = dataToSubmit.allocation_to_type_id_tags;
            }
            dataToSubmit.allow_override = override;
            const [err, response] = await to(start(dataToSubmit));
            setError(err);
            setResponse(response);
            setIds([]);
            if ((response as any).data.success === false) {
                setResponseDialogOpen(true);
            }
        },
        [allocationType, override, start, ids, filters, filtered, setIds],
    );

    const openAllocationDialog = useCallback(() => {
        filtered.setFalse();
        setConflictedIds(null);
        allocationState.setTrue();
    }, [filtered, allocationState, setConflictedIds]);

    const yesClicked = useCallback(() => {
        filtered.setFalse();
        requestReset();
        setOverride(true);
        setResponseDialogOpen(false);
    }, [requestReset, filtered]);

    const noClicked = useCallback(() => {
        if ((allocationResponse as any)?.data?.conflicts?.data?.length > 0) {
            setConflictedIds(
                (allocationResponse as any)?.data?.conflicts?.data?.map(conflict => conflict.id),
            );
        }
        filtered.setTrue();
        setResponseDialogOpen(false);
        allocationState.setFalse();
        reset();
        requestReset();
    }, [allocationState, reset, requestReset, filtered, setConflictedIds, allocationResponse]);

    const submitButtonDisabler = React.useMemo(() => {
        if (requestState === NetworkRequestState.InProgress) {
            return true;
        }
        if (typeOfAllocation == null) return true;
        if (screenToAllocateType == null) return true;

        if (screenToAllocateType === 'selected') {
            if (ids.length < 1) return true;
        }
        if (allocationType == null) return true;
        if (screenToAllocateType === 'filtered') {
            if (filters != null) {
                if (Object.keys(filters).length === 0) {
                    return true;
                }
                return false;
            }
        }

        return false;
    }, [requestState, screenToAllocateType, typeOfAllocation, ids, filters, allocationType]);

    const closeDialog = useCallback(() => {
        setConflictedIds(null);
        filtered.setFalse();
        setResponseDialogOpen(false);
        allocationState.setFalse();
        reset();
        requestReset();
    }, [allocationState, reset, requestReset, filtered, setConflictedIds]);

    return (
        <IfHasAllPermissions permissions={allocationPerms}>
            <DialogV2
                onClose={closeDialog}
                open={allocationState.value}
                maxWidth="xl"
                title="Modules.Displays.DisplaysList.allocationDialog.title"
            >
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <AutoGrid xs={5} gap={1}>
                        <Controller
                            name="allocation_type"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormLabel>
                                        {t(
                                            'Modules.Displays.DisplaysList.allocationDialog.allocationType',
                                        )}
                                    </FormLabel>
                                    <SelectField
                                        value={field.value}
                                        options={[
                                            {
                                                value: 'allocate',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.allocate',
                                                ),
                                            },
                                            {
                                                value: 'deallocate',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.deallocate',
                                                ),
                                            },
                                            {
                                                value: null,
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.empty',
                                                ),
                                            },
                                        ]}
                                        {...field}
                                    />
                                </>
                            )}
                        />
                        <Controller
                            name="screen_to_allocate_type"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormLabel>
                                        {t(
                                            'Modules.Displays.DisplaysList.allocationDialog.screensToAllocate',
                                        )}
                                    </FormLabel>
                                    <SelectField
                                        value={field.value}
                                        options={[
                                            {
                                                value: 'selected',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.selected',
                                                ),
                                            },
                                            {
                                                value: 'filtered',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.filtered',
                                                ),
                                            },
                                            {
                                                value: null,
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.empty',
                                                ),
                                            },
                                        ]}
                                        {...field}
                                    />
                                </>
                            )}
                        />
                        <Controller
                            name="allocation_to_type"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormLabel>
                                        {t(
                                            'Modules.Displays.DisplaysList.allocationDialog.allocateTo',
                                        )}
                                    </FormLabel>
                                    <SelectField
                                        value={field.value}
                                        options={[
                                            {
                                                value: 'packs',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.packs',
                                                ),
                                            },
                                            {
                                                value: 'tags',
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.tags',
                                                ),
                                            },
                                            {
                                                value: null,
                                                label: t(
                                                    'Modules.Displays.DisplaysList.allocationDialog.empty',
                                                ),
                                            },
                                        ]}
                                        {...field}
                                    />
                                </>
                            )}
                        />
                        <Controller
                            name="allocation_to_type_id_packs"
                            disabled={allocationType !== 'packs' || typeOfAllocation !== 'allocate'}
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormLabel>
                                        {t('Modules.Displays.DisplaysList.allocationDialog.packs')}
                                    </FormLabel>
                                    <AllEntitiesDropdown
                                        entityName="Displays_Pack"
                                        labelField="name"
                                        useLocalEntityData
                                        {...field}
                                    />
                                </>
                            )}
                        />
                        <Controller
                            name="allocation_to_type_id_tags"
                            disabled={allocationType !== 'tags' || typeOfAllocation !== 'allocate'}
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormLabel>
                                        {t('Modules.Displays.DisplaysList.allocationDialog.tags')}
                                    </FormLabel>
                                    <AllEntitiesDropdown
                                        entityName="Displays_Tag"
                                        labelField="name"
                                        useLocalEntityData
                                        {...field}
                                    />
                                </>
                            )}
                        />
                    </AutoGrid>
                    <Checkbox
                        label={t('Modules.Displays.DisplaysList.allocationDialog.allowOverride')}
                        checked={override}
                        onClick={() => setOverride(prevState => !prevState)}
                    />
                    <Box pt={2}>
                        <Box>
                            {requestState === NetworkRequestState.Error && (
                                <Alert severity="error">{String(error)}</Alert>
                            )}
                            {requestState === NetworkRequestState.InProgress && (
                                <CircularProgress size={25} />
                            )}
                            {requestState === NetworkRequestState.Success && (
                                <Typography color="success.main">
                                    <CheckCircle />
                                </Typography>
                            )}
                        </Box>
                        {responseDialogOpen && (
                            <Alert severity="warning">
                                {t('Modules.Displays.DisplaysList.allocationDialog.conflicts', {
                                    name: allocationType,
                                })}
                                <Box display="flex" justifyContent="center">
                                    <Stack gap={1} direction="row">
                                        <Button onClick={yesClicked}>
                                            {t(
                                                'Modules.Displays.DisplaysList.allocationDialog.override',
                                            )}
                                        </Button>
                                        <Button onClick={noClicked}>
                                            {t(
                                                'Modules.Displays.DisplaysList.allocationDialog.back',
                                            )}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Alert>
                        )}
                        {!responseDialogOpen && (
                            <Button type="submit" disabled={submitButtonDisabler}>
                                {t('Modules.Displays.DisplaysList.allocationDialog.submitBtn')}
                            </Button>
                        )}
                    </Box>
                </form>
            </DialogV2>
            <Button
                size="small"
                sx={{
                    minHeight: 26,
                    minWidth: 0,
                    px: 1,
                    py: 0.3,
                    fontSize: '0.72rem',
                    lineHeight: 1.1,
                }}
                onClick={openAllocationDialog}
            >
                {t('Modules.Displays.DisplaysList.allocation')}
            </Button>
        </IfHasAllPermissions>
    );
};

export default AllocationDialog;
